import React from "react";
import { ModalWrapper } from "./ModalWindow";
import LoadingIcon from "./LoadingIcon";
import GameLogo from "../Resources/logo.png";
import "./DiscordLinkWindow.css";

export type DiscordStatus = {
  linked: boolean;
  member: boolean;
  enforce: boolean;
  discordName: string | null;
};

type DiscordLinkWindowProps = {
  handleDismiss: () => void;
  status: DiscordStatus | null;
  refresh: () => Promise<DiscordStatus | null>;
  onError: (err: string) => void;
};

const box: React.CSSProperties = { padding: "24px 28px", textAlign: "center", minWidth: 340 };
const title: React.CSSProperties = { fontSize: 18, margin: "0 0 12px", color: "#fff" };
const desc: React.CSSProperties = { fontSize: 14, lineHeight: 1.6, color: "#bbb", margin: "0 0 20px" };
const primaryBtn: React.CSSProperties = {
  display: "block",
  width: "100%",
  padding: "12px 0",
  border: "none",
  borderRadius: 6,
  background: "#5865F2",
  color: "#fff",
  fontSize: 15,
  cursor: "pointer",
  marginBottom: 10,
};
const subBtn: React.CSSProperties = { ...primaryBtn, background: "#3a3d44" };

export default function DiscordLinkWindow({ handleDismiss, status, refresh, onError }: DiscordLinkWindowProps) {
  const [busy, setBusy] = React.useState(false);

  // "디스코드 연동하기" — 서버에서 OAuth URL 받아 기본 브라우저로 열기
  async function startLink() {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/discord/authUrl`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (!res.ok || !data.success || !data.url) {
        onError(data.msg || "연동 URL을 가져오지 못했습니다. 다시 시도해주세요.");
        return;
      }
      window.ipcRenderer.send("openExternal", data.url);
    } catch (e) {
      onError("연동 요청 중 오류가 발생했습니다.");
    } finally {
      setBusy(false);
    }
  }

  // "연동 확인" — 상태 새로고침
  async function recheck() {
    if (busy) return;
    setBusy(true);
    try {
      const next = await refresh();
      if (next && next.linked && next.member) {
        onError(""); // 성공 시 에러 메시지 정리
      }
    } finally {
      setBusy(false);
    }
  }

  let body: React.ReactNode;

  if (status && status.linked && status.member) {
    body = (
      <>
        <h2 style={title}>디스코드 연동 완료</h2>
        <p style={desc}>
          {status.discordName ? `${status.discordName} 계정과 ` : ""}연동되어 있으며 서버에 참여 중입니다.
          <br />
          게임을 정상적으로 시작할 수 있습니다.
        </p>
        <button style={primaryBtn} onClick={handleDismiss} disabled={busy}>
          닫기
        </button>
      </>
    );
  } else if (status && status.linked && !status.member) {
    body = (
      <>
        <h2 style={title}>디스코드 서버 참여 필요</h2>
        <p style={desc}>
          연동된 디스코드 계정이 서버에서 나갔거나 추방되어 게임을 시작할 수 없습니다.
          <br />
          디스코드 서버에 다시 참여한 뒤 아래 {'"연동 확인"'}을 눌러주세요.
        </p>
        <button style={subBtn} onClick={recheck} disabled={busy}>
          연동 확인
        </button>
      </>
    );
  } else {
    body = (
      <>
        <h2 style={title}>디스코드 연동이 필요합니다</h2>
        <p style={desc}>
          게임을 시작하려면 디스코드 계정 연동이 필요합니다.
          <br />
          {'"디스코드 연동하기"'}를 눌러 브라우저에서 인증한 뒤,
          <br />
          다시 돌아와 {'"연동 확인"'}을 눌러주세요.
          <br />
          <span style={{ fontSize: 12, color: "#888" }}>(디스코드 계정 1개당 테라 계정 최대 3개까지 연동 가능)</span>
        </p>
        <button style={primaryBtn} onClick={startLink} disabled={busy}>
          디스코드 연동하기
        </button>
        <button style={subBtn} onClick={recheck} disabled={busy}>
          연동 확인
        </button>
      </>
    );
  }

  return (
    <ModalWrapper handleDismiss={handleDismiss} className="Discord-Link-Window">
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div style={{ padding: "18px 0 4px" }}>
          <img src={GameLogo} alt="Logo" style={{ height: 40 }} />
        </div>
        <div style={box}>{body}</div>
      </div>
      {busy && <LoadingIcon dim />}
    </ModalWrapper>
  );
}
